import {
  OrderEventType,
  OrderIntent,
  OrderStatus,
  OrderType,
  SettlementType,
  TimeInForceOptions,
} from '@/modules/marketplace/types/marketplace';
import { CompanyInfo } from '@/modules/common/models';
import Decimal from 'decimal.js';
import { get as getPropValue } from 'lodash';
import { OmsOrder, VenueTopOfBookItem } from '@/modules/marketplace/models';
import { OmsOrderStatus } from '@/connect/gen/consts/omsconsts_pb';
import { RoundingRule } from '@/connect/gen/consts/loanconsts_pb';
import { RateType } from '@/connect';
import { VenueOrderForOms } from '@/connect/gen/modules/apiengine/services/venue/venue_pb';

export interface StatusResponse {
  label: Exclude<OrderStatus, 'FORCE_CANCELED'> | 'partial' | 'force canceled';
  color: 'gray' | 'warning' | 'green';
}

export function getStatus(order: OmsOrder): StatusResponse {
  const result: StatusResponse = {
    color: 'gray',
    label: order.status === 'FORCE_CANCELED' ? 'force canceled' : order.status,
  };

  if (order.status === 'OPEN') {
    if (order.filled > 0) {
      result.color = 'warning';
      result.label = 'partial';
    } else {
      result.color = 'green';
    }
  }

  return result;
}

export interface VenueStatusResponse {
  label: Exclude<keyof typeof OmsOrderStatus, 'FORCE_CANCELED'> | 'partial' | 'force canceled';
  color: 'gray' | 'warning' | 'green';
}

export function getVenueStatus(order: VenueOrderForOms): VenueStatusResponse {
  const result: VenueStatusResponse = {
    color: 'gray',
    label:
      order.orderStatus === OmsOrderStatus.FORCE_CANCELED
        ? 'force canceled'
        : (OmsOrderStatus[order.orderStatus] as VenueStatusResponse['label']),
  };

  if (order.orderStatus === OmsOrderStatus.OPEN) {
    if (order.totalExecutionQuantity > 0n) {
      result.color = 'warning';
      result.label = 'partial';
    } else {
      result.color = 'green';
    }
  }

  return result;
}

export function getEventTypeColor(event: OrderEventType): string {
  switch (event) {
    case 'CREATED':
      return 'green';
    case 'MODIFIED':
      return 'gray';
    case 'CANCELED':
      return 'gray';
    case 'EXPIRED':
      return 'gray';
    case 'EXECUTED':
      return 'orange';
    case 'CLOSED':
      return 'gray';
    case 'TERMINATED':
      return 'gray';
    case 'SPLIT_EXECUTED':
      return 'orange';
    case 'SPLIT_CANCELED':
      return 'gray';
    case 'FORCE_CANCELED':
      return 'gray';
    case 'SPLIT_FAILED':
      return 'red';
  }
}

// @TODO: should we use i18n for the helpers below?

const commonInForceOptions: TimeInForceOptions = [
  { value: 'DAY', text: 'Day', abbr: 'Day' },
  { value: 'GOOD_TILL_TIME', text: 'Good till time', abbr: 'GTT' },
  { value: 'GOOD_TILL_CANCEL', text: 'Good till cancel', abbr: 'GTC' },
];

export const marketTimeInForceOptions: TimeInForceOptions = [
  { value: 'IMMEDIATE_OR_CANCEL', text: 'Immediate or cancel', abbr: 'IOC' },
  { value: 'FILL_OR_KILL', text: 'Fill or kill', abbr: 'FOK' },
];

export const timeInForceOptions = [...commonInForceOptions, ...marketTimeInForceOptions];

export function timeInForceOptionsForOrder(
  orderType: OrderType,
  orderIntent: OrderIntent
): TimeInForceOptions {
  if (orderIntent === 'IOI') return commonInForceOptions;
  switch (orderType) {
    case 'MARKET':
      return marketTimeInForceOptions;
    default:
      return timeInForceOptions;
  }
}

export function timeInForceLabel(timeInForceType: string): string {
  const item = timeInForceOptions.find((i) => i.value === timeInForceType);
  return item ? item.text : '–';
}

export function timeInForceAbbr(timeInForceType: string): string {
  const item = timeInForceOptions.find((i) => i.value === timeInForceType);
  return item ? item.abbr : '–';
}

export function roundingRuleAbbr(rr: RoundingRule): string {
  switch (rr) {
    case RoundingRule.NO_ROUNDING:
      return 'no rounding';
    case RoundingRule.UP_TO_NEAREST_0_DOT_01:
      return 'up to nearest $0.01';
    case RoundingRule.UP_TO_NEAREST_0_DOT_05:
      return 'up to nearest $0.05';
    case RoundingRule.UP_TO_NEAREST_0_DOT_10:
      return 'up to nearest $0.10';
    case RoundingRule.UP_TO_NEAREST_0_DOT_25:
      return 'up to nearest $0.25';
    case RoundingRule.UP_TO_NEAREST_1_DOT_0:
      return 'up to nearest $1.00';
    default:
      return 'no rounding';
  }
}

export const orderTypeOptions: Array<{ value: OrderType; text: string }> = [
  { value: 'LIMIT', text: 'Limit' },
  { value: 'MARKET', text: 'Market' },
];

export const orderIntentOptions: Array<{ value: OrderIntent; text: string }> = [
  { value: 'FIRM', text: 'Firm' },
  { value: 'IOI', text: 'IOI' },
];

export const rateTypeOptions: Array<{ value: RateType; text: string }> = [
  { value: RateType.FEE, text: 'Fee' },
  { value: RateType.REBATE, text: 'Rebate Rate' },
];

// @TODO: remove me
export function orderTypeLabel(orderType: string): string {
  const item = orderTypeOptions.find((i) => i.value === orderType);
  return item ? item.text : '–';
}

export function orderIntentLabel(orderIntent: OrderIntent): string {
  switch (orderIntent) {
    case 'FIRM':
      return 'Firm';
    case 'IOI':
      return 'IOI';
    default:
      return '–';
  }
}

export function formatCounterparties(counterparties: CompanyInfo[] | null | undefined): string {
  if (!counterparties || counterparties.length === 0) {
    return '–';
  } else if (counterparties.length === 1) {
    return counterparties[0].companyName;
  } else {
    return '(Multi...)';
  }
}

export interface Tab {
  orderStatus?: OmsOrderStatus;
  hasExecuted?: boolean;
}

export const tabs: Record<'all' | 'open' | 'partial' | 'filled' | 'closed', Tab> = {
  all: {},
  open: { orderStatus: OmsOrderStatus.OPEN, hasExecuted: false },
  partial: { orderStatus: OmsOrderStatus.OPEN, hasExecuted: true },
  filled: { orderStatus: OmsOrderStatus.FILLED },
  closed: { orderStatus: OmsOrderStatus.CANCELED },
};

export function customSortTopOfBook(
  items: VenueTopOfBookItem[],
  indexes: [string],
  directions: [boolean]
): VenueTopOfBookItem[] {
  // multi-sort is disabled, we only need to look at the first index and direction
  const colName = indexes[0];
  const isDesc = directions[0];

  return items.sort((a: VenueTopOfBookItem, b: VenueTopOfBookItem) => {
    let aValue = getPropValue(a, colName);
    let bValue = getPropValue(b, colName);

    if (colName === 'lend.counterparties' || colName === 'borrow.counterparties') {
      // Consolidate the objects into strings
      aValue = formatCounterparties(aValue);
      bValue = formatCounterparties(bValue);
    }

    // Some columns are optional, so we also check for falsy values
    let beforeOrAfter = 0;
    if (aValue instanceof Decimal && bValue instanceof Decimal) {
      if (aValue.lessThan(bValue) || !aValue) {
        beforeOrAfter = -1;
      }
      if (aValue.greaterThan(bValue) || !bValue) {
        beforeOrAfter = 1;
      }
    } else {
      if (aValue < bValue || !aValue) {
        beforeOrAfter = -1;
      }
      if (aValue > bValue || !bValue) {
        beforeOrAfter = 1;
      }
    }

    // Found a difference, return it
    if (beforeOrAfter !== 0) {
      return beforeOrAfter * (isDesc ? -1 : 1);
    }

    // Previous sorting attempts tied (0), use security.ticker as a tie-breaker
    return (
      (a.security.ticker > b.security.ticker ? 1 : a.security.ticker < b.security.ticker ? -1 : 0) *
      (isDesc ? -1 : 1)
    );
  });
}

export const settlementTypeDisplayText: Record<SettlementType, string> = {
  NSCC: 'NSCC',
  BILATERAL: 'Bilateral',
  OCC: 'OCC',
};

export const settlementTypeColor: Record<SettlementType, string> = {
  NSCC: 'blue',
  BILATERAL: 'purple darken-2',
  OCC: 'deep-purple accent-1',
};
